import { Injectable, OnDestroy } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { lastValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BannerButton,
  BannerIconColor,
  BannerType,
  TopBanner
} from 'src/app/pages/new-billing/models/top-banner.model';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { BillingDetailsApiData } from 'src/app/pages/new-billing/types/api.types';
import { AuthSelectors } from 'src/app/store/auth/auth.selectors';
import { AuthCompany } from 'src/models';
import { SharedBannerComponent } from 'src/app/components/shared-banner/component';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { DismissBanner } from 'src/app/store/banner/actions';
import { CompanyService } from 'src/app/services/company/company.service';
import { SegmentService } from 'src/app/services/segment/segment.service';

type InfoBannerData = {
  showBanner: boolean;
  link: string;
  message: string;
};

@Injectable({ providedIn: 'root' })
export class InviteWebinarBanner implements TopBanner, OnDestroy {
  @Select(AuthSelectors.company) company$: Observable<AuthCompany>;
  @Select(NewBillingState.updatedBillingDetails) billingDetails$: Observable<BillingDetailsApiData>;
  @Select(state => state.banner.dismissedBanners['inviteWebinarBanner'])

  dismissed$: Observable<boolean>;
  currentCompany: AuthCompany;

  private readonly destroy$ = new Subject<void>();
  private subscription: Subscription;

  public ariaLabel = 'Webinar banner';
  public icon: string;
  public iconColor: BannerIconColor = 'white';
  public svgIcon: string;
  public trackingInfo: Record<string, unknown>;
  public type: BannerType = 'info';
  public bannerData: InfoBannerData = { showBanner: false, link: '', message: '' };

  constructor(
    private store: Store,
    public growthBook: GrowthBookService,
    public companyService: CompanyService,
    private segment: SegmentService,
  ) {
    this.initializeBannerInfo();
  }

  private async updateCompanySettings(settings) {
    if (this.currentCompany && this.currentCompany.id) {
      await lastValueFrom(this.companyService.putCompanySettings(this.currentCompany.id, settings));
    }
  }

  private initializeBannerInfo() {
    this.subscription = this.growthBook.getFeature('webinar-info-banner').pipe(
      map((info: InfoBannerData) => {
        return info;
      }),
    ).subscribe(info => {
      this.bannerData = info;
    });
  }

  get primaryButton(): BannerButton {
    return {
      text: 'Dismiss',
      action: async (banner: SharedBannerComponent) => {
        banner.dismiss();
        this.store.dispatch(new DismissBanner('inviteWebinarBanner'));
        await this.updateCompanySettings({ 'custom.dismissedBanners': { inviteWebinarBanner: true } });
        this.trackClickOnWebinarBannerButtons('Dismiss Banner', 'webinar-banner-dismiss');
      },
    };
  }

  get secondaryButton(): BannerButton {
    const { link } = this.bannerData;

    return {
      text: 'Register now',
      action: async (banner: SharedBannerComponent) => {
        window.open(link, '_blank');
        this.trackClickOnWebinarBannerButtons('Register Now', link);
      },
    };
  }

  get message(): string {
    if (this.bannerData && this.bannerData.link && this.bannerData.message) {
      const { message } = this.bannerData;
      return `${message}`;
    }
    return;
  }

  get showBanner(): boolean {
    return this.bannerData ? this.bannerData.showBanner : false;
  }

  public showCondition(): Observable<boolean> {
    return this.company$.pipe(
      map((company) => {
        this.currentCompany = company;

        const storedDismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '{}');
        const isDismissed = storedDismissedBanners['inviteWebinarBanner'] === true;
        const isDismissedInCompanySettings = company?.companySettings?.custom?.dismissedBanners?.inviteWebinarBanner === true;

        const isOwnerAndTrial = company?.role === 'owner' && company?.subscription?.status === 'trial';
        return isOwnerAndTrial && this.bannerData?.showBanner && !isDismissed && !isDismissedInCompanySettings;
      }),
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  trackClickOnWebinarBannerButtons(linkName: string, linkUrl: string) {
    this.segment.trackInAppEvents('Link Click', {
      linklocation: 'Webinar Banner',
      linkname: linkName,
      linktype: 'button',
      linkurl: linkUrl,
    });
  }
}
